/* eslint-disable react-hooks/rules-of-hooks */
import React, {
	Dispatch,
	Key,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Column, react } from "uikit";

import { Dispatcher, Language } from "../../services";
import { useTypedSelector } from "../../redux/store";
import {
	useObjectEditor,
	useDispatcherSubHandler,
	useTaxiServiceIdsDecoder,
	useCarParkFilterAccessControl,
} from "../../hooks";
import useModelSubscribe from "../../hooks/useModelSubscribe";
// import mapByKey from "../../utils/mapByKey";
import TableSelect from "../TableSelect";
import LightTable from "../LightTable";
import { TaxiServicesByIds, CompaniesByIds } from "../common";

import Root from "./components/Root";
import Header from "./components/Header";
import InternalController from "./Controller";

const defaultColumnIds = ["fullName", "role", "alias", "companies", "access"];

const DispatcherSelectTab = memo(
	react.withController<
		DispatcherSelectTab.PropsBase,
		DispatcherSelectTab.Controller
	>(
		({
			value,
			disabled,
			visible,
			language,
			allowedTaxiServiceIds,
			onChange,
			isOnFilterToCarPark = false,
			idCarParkFilter,
			roleTab,
		}) => {
			const lang = useTypedSelector((state) => state.session.language);
			const { t } = useTranslation();
			const valueEditor = useObjectEditor(value, onChange);
			const { getSortAndLimitDispatcherSubHandler } =
				useDispatcherSubHandler();
			const { subCarParkFilterAccess } =
				useCarParkFilterAccessControl<any>();

			const [filters, setFilters] = useState<Header.Filters.Value>({
				companyIds: ["all"],
				taxiServiceIds: ["all"],
				statuses: ["active", "blocked", "dismissed"],
				search: "",
			});

			const [isRoot, setIsRoot] = useState<number | undefined>(undefined);

			const decodeTaxiServiceIds = useTaxiServiceIdsDecoder();

			const [sort, setSort] = useState<TableSelect.Sort>({});
			const [columIds, setColumnIds] =
				useState<string[]>(defaultColumnIds);

			const modelSubscriptionOptions =
				useMemo<Dispatcher.SubscribeOptions>(() => {
					const result: Dispatcher.SubscribeOptions = {
						taxiServiceIds: decodeTaxiServiceIds(
							filters.companyIds,
							filters.taxiServiceIds,
						),
						statuses: filters.statuses,
						language,
						query: filters.search,
					};

					return result;
				}, [
					decodeTaxiServiceIds,
					filters.companyIds,
					filters.search,
					filters.statuses,
					filters.taxiServiceIds,
					language,
				]);

			const data = useModelSubscribe(
				modelSubscriptionOptions,
				Dispatcher,
			);

			const items = useMemo(() => {
				const options: Dispatcher.SubscribeOptions = {
					order:
						typeof sort.column === "string" &&
						typeof sort.type === "string"
							? ({
									[sort.column]: sort.type,
							  } as Dispatcher.SubscribeOptions["order"])
							: ({} as Dispatcher.SubscribeOptions["order"]),
				};

				const cacheDispatcherSub = data?.cache ?? [];

				if (roleTab) {
					const findRoot = cacheDispatcherSub.find(
						(item) => item?.root,
					);
					if (findRoot) {
						setIsRoot(findRoot.id);
					}
				}

				return getSortAndLimitDispatcherSubHandler(
					options,
					cacheDispatcherSub,
				);
			}, [
				data?.cache,
				getSortAndLimitDispatcherSubHandler,
				sort.column,
				sort.type,
				roleTab,
			]);

			const tableSelectOptions = useMemo(
				() => items.map((item) => ({ key: item.id, ...item })),
				[items],
			);
			const tableSelectValue = valueEditor.useGetter("dispatcherIds");
			const tableSelectOnChange = valueEditor.useSetter("dispatcherIds");

			const handlerOnChange = useCallback(
				(value: number[] = []) => {
					if (
						roleTab &&
						isRoot &&
						tableSelectValue.includes(isRoot) &&
						!value.includes(isRoot)
					) {
						tableSelectOnChange([isRoot, ...value]);
					} else if (
						roleTab &&
						isRoot &&
						!tableSelectValue.includes(isRoot) &&
						value.includes(isRoot)
					) {
						tableSelectOnChange(
							value.filter((item) => item !== isRoot),
						);
					} else {
						tableSelectOnChange(value);
					}
				},
				[tableSelectOnChange, isRoot, tableSelectValue, roleTab],
			);

			const tableSelectIsOptionActive = useCallback(
				(option: TableSelect.Option<Dispatcher.Model>) =>
					option.status === "active",
				[],
			);

			const tableSelectColumns = useMemo<TableSelect.Column[]>(
				() => [
					{
						id: "fullName",
						name: t("dispatcherSelectTab.fullName") ?? "",
						render: () => (
							<LightTable.Column flexGrow={1} sortable>
								<LightTable.HeaderCell verticalAlign="middle">
									{t("dispatcherSelectTab.fullName") ?? ""}
								</LightTable.HeaderCell>
								<LightTable.Cell
									verticalAlign="middle"
									dataKey="fullName"
								>
									{(item) =>
										`${item.person.lastName ?? ""} ${
											item.person.firstName ?? ""
										} ${
											item.person.fatherName ?? ""
										}`.trim()
									}
								</LightTable.Cell>
							</LightTable.Column>
						),
					},
					{
						id: "role",
						name: t("dispatcherSelectTab.role") ?? "",
						render: () => (
							<LightTable.Column width={140} sortable>
								<LightTable.HeaderCell>
									{t("dispatcherSelectTab.role") ?? ""}
								</LightTable.HeaderCell>
								<LightTable.Cell dataKey="role">
									{(rowData) =>
										rowData.roles
											?.map((role) => role.name)
											.join(", ") ?? ""
									}
								</LightTable.Cell>
							</LightTable.Column>
						),
					},

					{
						id: "alias",
						name: t("dispatcherSelectTab.alias") ?? "",
						render: () => (
							<LightTable.Column width={200} sortable>
								<LightTable.HeaderCell verticalAlign="middle">
									{t("dispatcherSelectTab.alias") ?? ""}
								</LightTable.HeaderCell>
								<LightTable.Cell
									verticalAlign="middle"
									dataKey="alias"
								>
									{(item) => item.alias}
								</LightTable.Cell>
							</LightTable.Column>
						),
					},

					{
						id: "companies",
						name: t("dispatcherSelectTab.companies") ?? "",
						render: () => (
							<LightTable.Column flexGrow={1} sortable>
								<LightTable.HeaderCell verticalAlign="middle">
									{t("dispatcherSelectTab.companies") ?? ""}
								</LightTable.HeaderCell>
								<LightTable.Cell
									verticalAlign="middle"
									dataKey="companies"
								>
									{(item) => {
										const content = useMemo(
											() => item.taxiServiceIds || [],
											[item.taxiServiceIds],
										);

										return (
											<CompaniesByIds
												taxiServiceIds={content}
												lang={lang}
											/>
										);
									}}
								</LightTable.Cell>
							</LightTable.Column>
						),
					},

					{
						id: "access",
						name: t("dispatcherSelectTab.access") ?? "",
						render: () => (
							<LightTable.Column flexGrow={1} sortable>
								<LightTable.HeaderCell verticalAlign="middle">
									{t("dispatcherSelectTab.access") ?? ""}
								</LightTable.HeaderCell>
								<LightTable.Cell
									verticalAlign="middle"
									dataKey="taxiService"
								>
									{(item) => {
										const content = useMemo(
											() => item.taxiServiceIds || [],
											[item.taxiServiceIds],
										);

										return (
											<TaxiServicesByIds
												taxiServiceIds={content}
												lang={lang}
												addNameCompany
												// alwaysShowCompanyName
											/>
										);
									}}
								</LightTable.Cell>
							</LightTable.Column>
						),
					},
				],
				[t, lang],
			);

			const modelsWithFilterCarPark = useMemo(() => {
				const dataAfterAccess = isOnFilterToCarPark
					? subCarParkFilterAccess({
							models: tableSelectOptions,
							branchPath: "carParks",
							branchPath2: "id",
							queryId: idCarParkFilter,
							needFilterWithQerry: true,
					  })
					: tableSelectOptions;

				return dataAfterAccess;
			}, [
				idCarParkFilter,
				isOnFilterToCarPark,
				subCarParkFilterAccess,
				tableSelectOptions,
			]);

			return (
				<Root hasPaddings={false} visible={visible}>
					<Column sizes="auto! 1fr" maxedWidth maxedHeight>
						<Header
							filters={filters}
							allowedTaxiServiceIds={allowedTaxiServiceIds}
							language={language}
							onChangeFilters={setFilters}
						/>
						<TableSelect
							value={tableSelectValue}
							sort={sort}
							columnIds={columIds}
							disabled={disabled}
							options={modelsWithFilterCarPark}
							defaultColumnIds={defaultColumnIds}
							isOptionActive={tableSelectIsOptionActive}
							Columns={tableSelectColumns}
							onChange={handlerOnChange as (value: Key[]) => void}
							onChangeSort={setSort}
							onChangeColumnIds={setColumnIds}
							disableOneCheckBox={isRoot}
						/>
					</Column>
				</Root>
			);
		},
		InternalController,
	),
);

declare namespace DispatcherSelectTab {
	type Ref = InternalController | null;

	type Controller = InternalController;

	interface Value {
		dispatcherIds: number[];
		id?: number;
	}

	interface PropsBase {
		value: Value;
		roleTab?: boolean;

		disabled: boolean;
		visible: boolean;
		language: Language;
		allowedTaxiServiceIds: number[];

		onChange: Dispatch<Value>;
		isOnFilterToCarPark?: boolean;
		idCarParkFilter?: number;
	}

	type Props = PropsBase & RefAttributes<Ref>;
}

export default DispatcherSelectTab;
